// TransmitterHistoryFaceplate.js
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./TransmitterHistoryFaceplate.css";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const RANGE_OPTIONS = [
  { label: "1m", ms: 60 * 1000 },
  { label: "15m", ms: 15 * 60 * 1000 },
  { label: "30m", ms: 30 * 60 * 1000 },
  { label: "1hr", ms: 60 * 60 * 1000 },
  { label: "2hr", ms: 2 * 60 * 60 * 1000 },
  { label: "5hr", ms: 5 * 60 * 60 * 1000 },
  { label: "10hr", ms: 10 * 60 * 60 * 1000 },
  { label: "1day", ms: 24 * 60 * 60 * 1000 },
  { label: "1wk", ms: 7 * 24 * 60 * 60 * 1000 },
  { label: "1month", ms: 30 * 24 * 60 * 60 * 1000 },
  { label: "1yr", ms: 365 * 24 * 60 * 60 * 1000 },
];

function TransmitterHistoryFaceplate({
  tag = "PT_0000",
  trendData = {},
  onClose = () => {},
  onOpenTrend = () => {},
}) {
  const [rangeMs, setRangeMs] = useState(60 * 1000);
  const [filterText, setFilterText] = useState("");
  const [dragging, setDragging] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  const [position, setPosition] = useState({
    x: 180,
    y: 80,
  });

  const dragRef = useRef({ dx: 0, dy: 0 });
  const faceplateRef = useRef(null);

  // ================= 🔥 FIX 1: HEARTBEAT =================
  const [forceTick, setForceTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setForceTick(t => t + 1);
    }, 1000); // ensures constant update even if value unchanged

    return () => clearInterval(interval);
  }, []);

  // ================= DRAG =================
  const startDrag = (e) => {
    setDragging(true);
    dragRef.current = {
      dx: e.clientX - position.x,
      dy: e.clientY - position.y,
    };
  };

  useEffect(() => {
    const move = (e) => {
      if (!dragging) return;

      setPosition({
        x: e.clientX - dragRef.current.dx,
        y: e.clientY - dragRef.current.dy,
      });
    };

    const up = () => setDragging(false);

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);

    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [dragging]);

  // ================= DATA =================
  const description =
    localStorage.getItem(`description_${tag}`) || "No description";

  const unit =
    localStorage.getItem(`unit_${tag}`) || "";

  const min = Number(localStorage.getItem(`min_${tag}`) ?? 0);
  const max = Number(localStorage.getItem(`max_${tag}`) ?? 100);

  // ================= 🔥 FIX 2: SAFE LIVE DATA EXTENSION =================
  const rawRecords = trendData[tag] || [];

  const records = useMemo(() => {
    if (!rawRecords.length) return [];

    const last = rawRecords[rawRecords.length - 1];

    return [
      ...rawRecords,
      {
        time: Date.now(),
        value: last.value, // keeps trend moving even if value unchanged
      },
    ];
  }, [rawRecords, forceTick]);

  // ================= FILTER =================
  const filteredData = useMemo(() => {
    let data = [...records];

    const from = Date.now() - rangeMs;

    data = data.filter((r) => r.time >= from);

    if (filterText.trim()) {
      const txt = filterText.toLowerCase();

      data = data.filter((r) => {
        const d = new Date(r.time);
        const s =
          d.toLocaleDateString() +
          " " +
          d.toLocaleTimeString();

        return s.toLowerCase().includes(txt);
      });
    }

    // 🔥 FIX 3: stable sorting (NO reverse)
    return data.sort((a, b) => a.time - b.time);
  }, [records, rangeMs, filterText]);

  const latest = useMemo(() => {
    if (!records.length) return null;
    return records[records.length - 1];
  }, [records, forceTick]);

  const formatDate = (ms) => new Date(ms).toLocaleDateString();
  const formatTime = (ms) => new Date(ms).toLocaleTimeString();

  // ================= CAPTURE =================
  const captureFaceplate = async () => {
    const tableWrap =
      faceplateRef.current.querySelector(".thf-table-wrap");

    const oldHeight = tableWrap.style.height;
    const oldMaxHeight = tableWrap.style.maxHeight;
    const oldOverflow = tableWrap.style.overflow;

    tableWrap.style.height = "auto";
    tableWrap.style.maxHeight = "none";
    tableWrap.style.overflow = "visible";

    await new Promise((r) => setTimeout(r, 200));

    const canvas = await html2canvas(faceplateRef.current, {
      scale: 2,
      backgroundColor: "#0b1f44",
      useCORS: true,
    });

    tableWrap.style.height = oldHeight;
    tableWrap.style.maxHeight = oldMaxHeight;
    tableWrap.style.overflow = oldOverflow;

    return canvas;
  };

  // ================= SHARE =================
  const handleSharePDF = async () => {
    try {
      setShowShareMenu(false);

      const scrollWrap =
        faceplateRef.current.querySelector(".thf-table-wrap");

      const oldHeight = scrollWrap.style.height;
      const oldMaxHeight = scrollWrap.style.maxHeight;
      const oldOverflow = scrollWrap.style.overflow;

      scrollWrap.style.height = "auto";
      scrollWrap.style.maxHeight = "none";
      scrollWrap.style.overflow = "visible";

      await new Promise((r) => setTimeout(r, 300));

      const canvas = await captureFaceplate();

      scrollWrap.style.height = oldHeight;
      scrollWrap.style.maxHeight = oldMaxHeight;
      scrollWrap.style.overflow = oldOverflow;

      const img = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = (canvas.height * pageWidth) / canvas.width;

      pdf.addImage(img, "PNG", 0, 0, pageWidth, pageHeight);

      const blob = pdf.output("blob");

      const file = new File([blob], `${tag}_trend_report.pdf`, {
        type: "application/pdf",
      });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: `${tag} Trend Report`,
          files: [file],
        });
        return;
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = `${tag}_trend_report.pdf`;
      a.click();

      URL.revokeObjectURL(url);
    } catch (err) {
      console.log(err);
    }
  };

  // ================= CSV =================
  const handleCSVExport = () => {
    const csv = [
      ["S/N", "Date", "Time", "Value", "Unit"],
      ...filteredData.map((r, i) => [
        i + 1,
        formatDate(r.time),
        formatTime(r.time),
        Number(r.value).toFixed(2),
        unit,
      ]),
    ]
      .map((r) => r.join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = `${tag}_History.csv`;
    a.click();

    URL.revokeObjectURL(url);
  };

  return (
    <div
      ref={faceplateRef}
      className="thf-container"
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      {/* HEADER */}
      <div className="thf-header" onMouseDown={startDrag}>
        <button className="thf-close" onClick={onClose}>
          ✕
        </button>

        <div className="thf-title">{tag}</div>
      </div>

      {/* RANGE */}
      <div className="thf-ranges">
        {RANGE_OPTIONS.map((r) => (
          <button
            key={r.label}
            className={
              rangeMs === r.ms
                ? "thf-range active"
                : "thf-range"
            }
            onClick={() => setRangeMs(r.ms)}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* SEARCH */}
      <div className="thf-search-wrap">
        <input
          className="thf-search"
          placeholder="Search date/time"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />
      </div>

      {/* TABLE */}
      <div className="thf-table-wrap">
        <table className="thf-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Date</th>
              <th>Time</th>
              <th>Value</th>
              <th>Unit</th>
            </tr>
          </thead>

          <tbody>
            {filteredData.map((r, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>{formatDate(r.time)}</td>
                <td>{formatTime(r.time)}</td>
                <td>{Number(r.value).toFixed(2)}</td>
                <td>{unit}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* INFO */}
      <div className="thf-info">
        <div>
          <strong>Description:</strong> {description}
        </div>

        <div>
          <strong>Range:</strong> {min} to {max} {unit}
        </div>

        <div>
          <strong>Current:</strong>{" "}
          {latest ? Number(latest.value).toFixed(2) : "--"} {unit}
        </div>

        <div>
          <strong>Now:</strong> {new Date().toLocaleString()}
        </div>
      </div>

      {/* FOOTER */}
      <div className="thf-footer">
        <button className="thf-btn" onClick={() => setShowShareMenu(true)}>
          Share
        </button>

        <button className="thf-btn" onClick={handleCSVExport}>
          CSV
        </button>

        <button className="thf-btn" onClick={handleSharePDF}>
          PDF
        </button>

        <button className="thf-btn trend" onClick={onOpenTrend}>
          Open Trend
        </button>
      </div>

      {/* SHARE */}
      {showShareMenu && (
        <div className="thf-share-overlay">
          <div className="thf-share-popup">
            <div className="thf-share-title">Share Report</div>

            <button className="thf-share-btn" onClick={handleSharePDF}>
              📄 PDF
            </button>

            <button className="thf-share-close" onClick={() => setShowShareMenu(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransmitterHistoryFaceplate;